import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import PDPProtectionPlanSelector from 'components/consumer/PDP/PDPProtectionPlanSelector';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import useIsBrowser from 'hooks/useIsBrowser';
import PDPDesktopPhotoPanel from '../PDPDesktopPhotoPanel';
import PDPNameSection from '../PDPNameSection';
import PDPShopTabsBar from '../PDPShopTabsBar';
import PDPAvailableInSizes from '../PDPAvailableInSizes';
import PDPOnDisplayInShowroom from '../PDPOnDisplayInShowroom';
import PDPAddToBagSection from '../PDPAddToBagSection';
import PDPCompleteOrder from '../PDPCompleteOrder';
import PDPUpSellProduct from '../PDPUpSellProduct';
import PDPSellingAttributes from '../PDPSellingAttributes';
import PDPProductDescription from '../PDPProductDescription';
import PDPDimensionsAndDetails from '../PDPDimensionsAndDetails';
import PDPCustomerPhotos from '../PDPCustomerPhotos';
import PDPBreadCrumbs from '../PDPBreadCrumbs';
import PDPPress from '../PDPPress';
import PDPAlertBar from '../PDPAlertBar';
import PDPAlertsSection from '../PDPAlertsSection';
import PDPDiscontinuedMessage from '../PDPDiscontinuedMessage';
import PDPChooseYourOptions from '../PDPChooseYourOptions';
import PDPDesktopPlaceholder from '../PDPDesktopPlaceholder';

const PDPCollectionSlider = loadable(() => import('../PDPCollectionSlider'));

const ModularAddProductsMarxent = loadable(() =>
  import('components/consumer/modular-configurator/ModularAddProducts/marxent')
);

const PDPDesktopInner = ({
  discontinuedCategoryMsg = null,
  pdpIsModularBYOProduct = false,
  isEditOrder,
  isEFGInternal = false,
  isOutOfStock = false,
  showDiscontinuedNotification = false,
  showOutOfStockNotification = {},
  slug,
}) => {
  const isBrowser = useIsBrowser();

  const [efgInternalKey, setEfgInternalKey] = useState('0');
  const [displayPressComp, setDisplayPressComp] = useState(false);
  const [marxentSelectedFabric, setMarxentSelectedFabric] = useState(null);
  const [hideMarxent, setHideMarxent] = useState(true);
  const [unavailableFabric, setUnavailableFabric] = useState(false);

  useEffect(() => {
    if (isEFGInternal && !isNewConsumerApp) {
      setEfgInternalKey('1');
    }
  }, [isEFGInternal]);

  return (
    <FixedWidthContainer
      wide
      className="flex flex-row-reverse !pt-2 !pb-8 max-[1023px]:hidden"
      data-xc="PDPDesktop"
    >
      <div
        className="flex-none min-w-[460px] w-[460px] [@media(width:1024px)]:min-w-[400px] [@media(width:1024px)]:w-[400px]"
        key={efgInternalKey}
      >
        <PDPNameSection isEditOrder={isEditOrder} />

        {!isEditOrder && (!isNewConsumerApp || isBrowser) ? (
          <PDPAvailableInSizes />
        ) : (
          <div className="PDPDesktop__PDPAvailableInSizes-filler" />
        )}

        <PDPOnDisplayInShowroom />

        {showDiscontinuedNotification ? (
          <PDPAlertBar type="error">
            <PDPDiscontinuedMessage
              discontinuedCategoryMsg={discontinuedCategoryMsg}
            />
          </PDPAlertBar>
        ) : (
          <div className="PDPDesktop__PDPAlertBar-filler" />
        )}

        {!isOutOfStock && !showDiscontinuedNotification ? (
          <div className="PDPDesktop__in-stock-options">
            {pdpIsModularBYOProduct && (
              <div className="border-t border-gray-light7">
                <SuspenseWithLoader>
                  <ModularAddProductsMarxent
                    marxentSelectedFabric={marxentSelectedFabric}
                    unavailableFabric={unavailableFabric}
                    setHideMarxent={setHideMarxent}
                    setUnavailableFabric={setUnavailableFabric}
                    key={slug}
                  />
                </SuspenseWithLoader>
              </div>
            )}

            <PDPShopTabsBar isEditOrder={isEditOrder} />

            <PDPChooseYourOptions
              isEditOrder={isEditOrder}
              setMarxentSelectedFabric={setMarxentSelectedFabric}
            />

            {!isEditOrder && <PDPUpSellProduct />}

            {!isEditOrder && !pdpIsModularBYOProduct && (
              <PDPProtectionPlanSelector />
            )}
          </div>
        ) : (
          <div className="PDPDesktop__in-stock-options-filler" />
        )}

        {isOutOfStock && !showDiscontinuedNotification ? (
          <PDPAlertsSection
            showOutOfStockNotification={showOutOfStockNotification}
          />
        ) : (
          <div className="PDPDesktop__PDPAlertSection-filler" />
        )}

        {isEditOrder ? (
          <PDPCompleteOrder />
        ) : (
          <PDPAddToBagSection
            isOutOfStock={isOutOfStock}
            isEditOrder={isEditOrder}
          />
        )}

        <PDPSellingAttributes />

        {!isEditOrder ? (
          <PDPProductDescription />
        ) : (
          <div className="PDPDesktop__PDPProductDescription-filler" />
        )}

        {!pdpIsModularBYOProduct ? (
          <PDPDimensionsAndDetails />
        ) : (
          <div className="PDPDesktop__PDPDimensionsAndDetails-filler" />
        )}

        {!isEditOrder ? (
          <PDPCustomerPhotos slug={slug} />
        ) : (
          <div className="PDPDesktop__PDPCustomerPhotos-filler" />
        )}
        {!displayPressComp ? (
          <SuspenseWithLoader>
            <PDPCollectionSlider setDisplayPressComp={setDisplayPressComp} />
          </SuspenseWithLoader>
        ) : (
          <PDPPress />
        )}
      </div>

      <div className="flex-1 mr-12">
        <PDPBreadCrumbs />

        <PDPDesktopPhotoPanel
          slug={slug}
          isEditOrder={isEditOrder}
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
          hideMarxent={hideMarxent}
          unavailableFabric={unavailableFabric}
        />
      </div>
    </FixedWidthContainer>
  );
};

PDPDesktopInner.propTypes = {
  discontinuedCategoryMsg: PropTypes.shape({
    category: PropTypes.string,
    link: PropTypes.string,
  }),
  isEditOrder: PropTypes.bool,
  isEFGInternal: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  showDiscontinuedNotification: PropTypes.bool,
  showOutOfStockNotification: PropTypes.shape({
    productId: PropTypes.number,
    show: PropTypes.bool,
  }),
  slug: PropTypes.string,
};

const PDPDesktop = props => (
  <SuspenseWithLoader fallback={<PDPDesktopPlaceholder />}>
    <PDPDesktopInner {...props} />
  </SuspenseWithLoader>
);

export default PDPDesktop;
